/**
 * M7 S32: the Logs screen (spec §2.6): the Case line, tabs by source (each with an unread mark, never a count), the
 * open leads and questions, the logs themselves and Your records. It renders a `LogsView` and a `LogsScreenState`
 * and dispatches `LogsAction`s; every rule is in `models/`. The focused control carries `data-focused` (keyboard
 * and pad focus are the model's, not the DOM's). UI events only.
 */
import type { ReactNode } from 'react'
import { CaseLine } from './CaseLine'
import { Leads } from './Leads'
import { EntryList, LogView } from './LogView'
import type { LogsConfig } from './models/config'
import type { LogsView } from './models/logs'
import { entryOn, type LogsAction, type LogsScreenState } from './models/screen'
import { YourRecords } from './YourRecords'
import '../strategic/strategic.css'
import './logs.css'

export interface LogsProps {
  view: LogsView
  state: LogsScreenState
  config: LogsConfig
  dispatch: (a: LogsAction) => void
  /** In a dashboard section (card M3): the shell gives the frame, title and Close, so the screen drops its own. */
  embedded?: boolean
}

export function Logs(p: LogsProps): ReactNode {
  const { view, state: s, config: c } = p
  const embedded = p.embedded === true
  const row = s.zone === 'list' && s.openLog === null ? s.row : -1
  const open = s.openLog === null ? null : entryOn(view, s.tab, s.openLog)
  const press = (index: number): void => p.dispatch({ type: 'pressRow', index })

  let body: ReactNode
  if (open !== null) {
    body = <LogView entry={open} config={c} focused onBack={() => p.dispatch({ type: 'closeLog' })} />
  } else if (s.tab === 'case') {
    // M7 S34: the claim draft row comes first when a claim can be drafted; the leads follow it.
    // M7 S39: the finale row follows it while a finale path is on offer.
    const draft = view.claimDraft ?? null
    const finale = view.finaleRow ?? null
    const finaleAt = draft === null ? 0 : 1
    const offset = finaleAt + (finale === null ? 0 : 1)
    body = (
      <>
        {view.empty ? <p className="sui-empty">{c.empty.screen}</p> : null}
        {draft === null ? null : (
          <section className="lg-section">
            <button
              type="button"
              tabIndex={-1}
              className="sui-btn lg-btn"
              data-testid="logs-claim-draft"
              {...(row === 0 ? { 'data-focused': '' } : {})}
              onClick={() => press(0)}
            >
              {draft}
            </button>
          </section>
        )}
        {finale === null ? null : (
          <section className="lg-section">
            <button
              type="button"
              tabIndex={-1}
              className="sui-btn lg-btn"
              data-testid="logs-finale"
              {...(row === finaleAt ? { 'data-focused': '' } : {})}
              onClick={() => press(finaleAt)}
            >
              {finale}
            </button>
          </section>
        )}
        <Leads leads={view.leads} questions={view.questions} config={c} focusedRow={row - offset} onSetNav={(i) => press(i + offset)} />
      </>
    )
  } else if (s.tab === 'records') {
    body = <YourRecords records={view.records} config={c} focusedRow={row} onOpen={press} />
  } else {
    body = <EntryList entries={view.entries[s.tab] ?? []} config={c} focusedRow={row} onOpen={press} />
  }

  return (
    <div
      className={embedded ? 'lg-screen lg-screen--embedded' : 'sui lg-screen'}
      data-testid="logs-screen"
      role={embedded ? undefined : 'dialog'}
      aria-label={view.title}
    >
      {embedded ? null : (
        <div className="sui-head">
          <h2 className="sui-title">{view.title}</h2>
          <button
            type="button"
            tabIndex={-1}
            className="sui-btn lg-btn"
            data-testid="logs-close"
            onClick={() => p.dispatch({ type: 'nav', input: 'back' })}
          >
            {c.close}
          </button>
        </div>
      )}
      <CaseLine view={view.caseLine} config={c} />
      <div className="lg-tabs" role="tablist" data-testid="logs-tabs">
        {view.tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            tabIndex={-1}
            className="sui-btn lg-btn lg-tab"
            data-testid="logs-tab"
            data-tab={t.id}
            aria-selected={t.id === s.tab}
            {...(s.zone === 'tabs' && t.id === s.tab ? { 'data-focused': '' } : {})}
            onClick={() => p.dispatch({ type: 'selectTab', tab: t.id })}
          >
            {t.label}
            {t.unread ? <span className="lg-mark">{c.unreadTab}</span> : null}
          </button>
        ))}
      </div>
      <div className="lg-body">{body}</div>
      {s.notice === null ? null : (
        <p className="lg-notice" data-testid="logs-notice" role="status">
          {s.notice}
        </p>
      )}
    </div>
  )
}
